import { ALL_VALUES } from "../config"
import {
    AmbientestLogbookRecord,
    CatalogKeyType,
    CatalogRootType,
    CatalogType,
    CredentialType,
    DashboardProject,
    NavItemDataProps,
    RecordOption,
    RepositoryGroup,
    ResourceType,
    ResourceVersionType,
    SartokenLogbookRecord,
    SearchItem,
    UrlSearchBuilder,
    UserRolType,
    ValidateMatchParam
} from "../types"
import businessHelper from "./business.helper"
import { mapper } from "./mapper.helper"

const matchParamsDefault: ValidateMatchParam = {
    caseSensitive: false,
    fromStart: false
}
function validateMatch(text: string, search: string, parameters: ValidateMatchParam) {
    let data = text || ''
    let dataToValidate = parameters.caseSensitive ? data : data.toLowerCase()
    let searchToValidate = parameters.caseSensitive ? search : search.toLowerCase()
    return parameters.fromStart ? dataToValidate.startsWith(searchToValidate) : dataToValidate.includes(searchToValidate)
}

function filterSearchResource(resource: ResourceType, search: string, matchParams: ValidateMatchParam) {
    return validateMatch(resource.name, search, matchParams) ||
        validateMatch(resource.label, search, matchParams) ||
        validateMatch(resource.description, search, matchParams)
}

function filterSearchResourceVersion(version: ResourceVersionType, search: string, matchParams: ValidateMatchParam, aforeList?: CatalogType[]) {
    let afores = version.afores.join(',')
    let aforesName = version.afores.map(v => aforeList?.find(a => a.value === v)?.label).join(',')
    return validateMatch(version.version, search, matchParams) || validateMatch(version.note, search, matchParams)
        || validateMatch(afores, search, matchParams) || validateMatch(aforesName, search, matchParams)
        || (afores === ALL_VALUES && validateMatch(aforeList?.map(a => a.label).join(',') || '', search, matchParams))
        || (afores === ALL_VALUES && validateMatch(aforeList?.map(a => a.value).join(',') || '', search, matchParams))
}

function filterSearchLogbook(item: SartokenLogbookRecord, serviceList: RecordOption[], search: string, matchParams: ValidateMatchParam) {
    return validateMatch(item.curp, search, matchParams) || validateMatch(item.date, search, matchParams) ||
        validateMatch(item.exception || '', search, matchParams) || validateMatch(item.id.toString(), search, matchParams) ||
        validateMatch(item.idClient, search, matchParams) || validateMatch(item.idProyect, search, matchParams) ||
        validateMatch(item.message, search, matchParams) || validateMatch(serviceList.find(s => Number(s.id) === item.serviceId)?.label || '', search, matchParams)
}


function filterSearchAmbientestLogbook(item: AmbientestLogbookRecord, search: string, matchParams: ValidateMatchParam) {
    return validateMatch(item.curp, search, matchParams) || validateMatch(item.date, search, matchParams) ||
        validateMatch(item.envType.label, search, matchParams) || validateMatch(item.id.toString(), search, matchParams) ||
        validateMatch(item.message || '', search, matchParams) || validateMatch(item.status, search, matchParams) ||
        validateMatch(item.userName, search, matchParams)
}

function searchPageMatches(search: string, matchParams: ValidateMatchParam, data: NavItemDataProps[], parent?: NavItemDataProps): SearchItem[] {
    let matches: SearchItem[] = []
    for (let i of data) {
        if ((i.path && validateMatch(i.path, search, matchParams)) ||
            validateMatch(i.label, search, matchParams) ||
            (i.description && validateMatch(i.description, search, matchParams))
        ) {
            matches = [...matches, {
                description: i.description || '',
                icon: i.icon || parent?.icon || 'file-lines',
                keywords: parent ? [parent.label] : [],
                link: i.path || '/',
                title: i.label
            }]
        }
        if (i.items) {
            matches = [...matches, ...searchPageMatches(search, matchParams, i.items, i)]
        }
    }

    return matches
}


function searchInRepository(search: string, urlBuilder: UrlSearchBuilder<CredentialType>, matchParams: ValidateMatchParam, data?: CredentialType[]): SearchItem[] {
    let matches: SearchItem[] = []
    let keywords = ['repositorio', 'credenciales', 'workspace', 'token', 'config']
    data?.forEach((credential) => {
        let toSearchItem = mapper.toSearchCredential(keywords, urlBuilder)
        if (validateMatch(credential.workspace, search, matchParams) ||
            validateMatch(credential.description, search, matchParams) ||
            keywords.includes(search.toLowerCase())) {
            matches.push(toSearchItem(credential))
        } else {
            credential.repositories.forEach(repo => {
                if (validateMatch(repo.name, search, matchParams)
                    || validateMatch(repo.description, search, matchParams)
                    || validateMatch(repo.subtype, search, matchParams)
                    || validateMatch(repo.path, search, matchParams)) {
                    let toSearchRepoItem = mapper.toSearchRepository(keywords, urlBuilder, repo)
                    matches.push(toSearchRepoItem(credential))
                }
            })
        }
    })
    return matches
}

function searchInCatalog(search: string, urlBuilder: UrlSearchBuilder<CatalogType>, matchParams: ValidateMatchParam, data?: CatalogRootType): SearchItem[] {
    let matches: SearchItem[] = []
    let keywords = ['catalogo', 'config', 'workspace']
    data?.BASE.forEach(group => {
        let toSearchItem = mapper.toSearchCatalog(keywords, urlBuilder, String(group.value))
        if (validateMatch(String(group.value), search, matchParams) ||
            validateMatch(group.label, search, matchParams) ||
            keywords.includes(search.toLowerCase())
        ) {
            matches.push(toSearchItem(group))
        } else {
            data[group.value as CatalogKeyType].forEach(item => {
                if (validateMatch(String(item.value), search, matchParams) ||
                    validateMatch(item.label, search, matchParams)) {
                    matches.push(toSearchItem(item))
                }
            })
        }

    })
    return matches
}

function searchInSoap(search: string, urlBuilder: UrlSearchBuilder<ResourceType>, matchParams: ValidateMatchParam, data?: ResourceType[]): SearchItem[] {
    let matches: SearchItem[] = []
    let keywords = ['soap', 'api']
    data?.forEach((item) => {
        let nameGroup = item.name.split('-')[0]
        let toSearchItem = mapper.toSearchResource([nameGroup, ...keywords], urlBuilder, nameGroup)
        if (keywords.includes(search.toLowerCase())) {
            matches = [...matches, toSearchItem(item)]
        } else {
            if (validateMatch(nameGroup, search, matchParams) ||
                validateMatch(item.label, search, matchParams) ||
                validateMatch(item.description, search, matchParams)
            ) {
                matches = [...matches, toSearchItem(item)]
            }
        }
    })
    return matches
}

function searchInRest(search: string, urlBuilder: UrlSearchBuilder<ResourceType>, matchParams: ValidateMatchParam, data?: Map<string, ResourceType[]>): SearchItem[] {
    let matches: SearchItem[] = []
    let keywords = ['rest', 'api']
    data?.forEach((value, key) => {
        let toSearchItem = mapper.toSearchApidoc([key, ...keywords], urlBuilder, key)
        if (validateMatch(key, search, matchParams) || keywords.includes(search.toLowerCase())) {
            let mapToSearchItem: SearchItem[] = value.map(toSearchItem)
            matches = [...matches, ...mapToSearchItem]
        } else {
            value.forEach(api => {
                let path = api.versions[0]?.artifacts[0]?.path || ''
                if (validateMatch(api.name, search, matchParams) || validateMatch(path, search, matchParams)
                    || validateMatch(api.description, search, matchParams)) {
                    matches.push(toSearchItem(api))
                }
            })
        }
    })
    return matches
}

function searchInResource(
    search: string,
    urlBuilder: UrlSearchBuilder<ResourceType>,
    urlVersionBuilder: UrlSearchBuilder<ResourceVersionType>,
    matchParams: ValidateMatchParam,
    data?: RepositoryGroup<ResourceType>[],
    aforeList?: CatalogType[]
): SearchItem[] {
    let matches: SearchItem[] = []
    let keywords = ['sdk']
    data?.forEach((group, idx) => {
        let toSearchItem = mapper.toSearchResource([group.label, ...keywords], urlBuilder, String(idx))
        if (validateMatch(group.label, search, matchParams) || keywords.includes(search.toLowerCase())) {
            let mapToSearchItem: SearchItem[] = group.resources.map(toSearchItem)
            matches = [...matches, ...mapToSearchItem]
        } else {
            group.resources.forEach(resource => {
                if (filterSearchResource(resource, search, matchParams)) {
                    matches.push(toSearchItem(resource))
                } else {
                    resource.versions.every(version => {
                        let isMatch = filterSearchResourceVersion(version, search, matchParams, aforeList)
                        if (isMatch) {
                            let versionToSearchItem = mapper.toSearchResourceVersion([group.label, ...keywords], urlVersionBuilder, version, String(idx))
                            matches.push(versionToSearchItem(resource))
                        }
                        return !isMatch
                    })
                }
            })
        }
    })
    return matches
}

function searchResources(
    collection: ResourceType[],
    keyword: string,
    matchParams: ValidateMatchParam,
    aforeList?: CatalogType[]
) {
    let search = keyword.toLocaleLowerCase()
    let filterCollection: ResourceType[] = collection
    if (search.length > 2) {
        filterCollection = collection.filter(c => filterSearchResource(c, search, matchParams) ||
            c.versions.reduce((prev, currentVal) => prev || filterSearchResourceVersion(currentVal, search, matchParams, aforeList), false))
    }
    return filterCollection
}

function searchRol(search: string, urlBuilder: UrlSearchBuilder<UserRolType>, matchParams: ValidateMatchParam, data?: UserRolType[]): SearchItem[] {
    let matches: SearchItem[] = []
    data?.forEach(d => {
        let keywords = ['roles', 'acceso', 'menu', d.name]
        let toSearchItem = mapper.toSearchRolMenu(keywords, urlBuilder, String(d.id))
        if (validateMatch(d.name, search, matchParams) ||
            keywords.includes(search.toLowerCase())
        ) {
            matches.push(toSearchItem(d))
        } else {
            mapper.menuListToArray(d.menu, 0).forEach(item => {
                if (validateMatch(item.label, search, matchParams) ||
                    validateMatch(item.path || item.label, search, matchParams)) {
                    matches.push(toSearchItem(d))
                }
            })
        }

    })
    return matches
}

function searchLinkedResourceAdmin(search: string, urlBuilder: UrlSearchBuilder<ResourceType>, matchParams: ValidateMatchParam, data?: ResourceType[], roles?: UserRolType[]): SearchItem[] {
    let matches: SearchItem[] = []
    let keywords = ['recursos vinculados']
    data?.forEach((item) => {
        let rolesData = businessHelper.getMenuRolData(item.linkedMenus || [], roles || [])
        keywords = [...keywords, ...rolesData.map(r => r.rol), ...rolesData.map(r => r.label)]
        let toSearchItem = mapper.toSearchResource(keywords, urlBuilder, '')
        if (keywords.map(r => r.toLowerCase()).includes(search.toLowerCase())) {
            matches = [...matches, toSearchItem(item)]
        } else {
            if (validateMatch(item.label, search, matchParams) ||
                validateMatch(item.description, search, matchParams)
            ) {
                matches = [...matches, toSearchItem(item)]
            }
        }
    })
    return matches
}

function searchLinkedResource(search: string, urlBuilder: UrlSearchBuilder<ResourceType>, matchParams: ValidateMatchParam, data?: ResourceType[], menu?: NavItemDataProps[]): SearchItem[] {
    let matches: SearchItem[] = []
    let keywords = ['recursos vinculados']
    data?.forEach((item) => {
        let menuItem = mapper.menuListToArray((menu || []), 0).find(m => item.linkedMenus?.includes(m.id))
        let nameGroup = menuItem?.label || ''
        let toSearchItem = mapper.toSearchResource(keywords, urlBuilder, menuItem?.path || '')
        if (keywords.includes(search.toLowerCase())) {
            matches = [...matches, toSearchItem(item)]
        } else {
            if (validateMatch(nameGroup, search, matchParams) ||
                validateMatch(item.label, search, matchParams) ||
                validateMatch(item.description, search, matchParams) ||
                validateMatch(item.description, search, matchParams)
            ) {
                matches = [...matches, toSearchItem(item)]
            }
        }
    })
    return matches
}

function filterJiraProject(item: DashboardProject, search: string, matchParams: ValidateMatchParam) {
    return validateMatch(item.key, search, matchParams) || validateMatch(item.name, search, matchParams) ||
        validateMatch(item.type || '', search, matchParams) || validateMatch(item.avatar.toString(), search, matchParams)
}

const matchHelper = {
    validateMatch,
    filterSearchLogbook,
    filterSearchAmbientestLogbook,
    filterJiraProject,
    matchParamsDefault,
    searchPageMatches,
    searchInRepository,
    searchInResource,
    searchInCatalog,
    searchInSoap,
    searchInRest,
    searchResources,
    searchRol,
    searchLinkedResource,
    searchLinkedResourceAdmin,
    regexp: {
        curp: /^[A-Z]{4}\d{6}[A-Z\d]{8}$/i,
        mail: /^[a-z0-9._-]{2,30}(?!\.)@{1}(?!\.)(?!_)[a-z0-9_-]{2,25}[.]{1}[a-z0-9]{2,5}[.]*([a-z0-9]{2,5}|[a-z0-9]{0})$/i
    }
}

export default matchHelper